import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';

const ANOMALY_COLOR = '#c0392b';
const NORMAL_COLOR = '#5dade2';

/**
 * Horizontal unusualness bar chart (Isolation Forest watchlist).
 * Red bars = ANOMALY; blue = normal. Mirrors the notebook PDF chart 1.
 */
export default function UnusualnessBarChart({
  watchlist = [],
  series = '',
  heightPerBar = 22,
  minHeight = 160,
  maxHeight = 640,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const rows = useMemo(() => {
    const list = Array.isArray(watchlist) ? [...watchlist] : [];
    return list.sort(
      (a, b) => Number(a.unusualness_score ?? 0) - Number(b.unusualness_score ?? 0),
    );
  }, [watchlist]);

  // Responsive width via ResizeObserver (handles sidebar open/close).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    let frame = null;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (containerRef.current) {
          setContainerWidth(containerRef.current.getBoundingClientRect().width);
        }
      });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    el.addEventListener('transitionend', update);

    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
      el.removeEventListener('transitionend', update);
    };
  }, []);

  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl) return undefined;

    const width = Math.max(containerWidth || 640, 320);
    const chartHeight = Math.min(
      maxHeight,
      Math.max(minHeight, rows.length * heightPerBar + 48),
    );
    const margin = { top: 12, right: 24, bottom: 36, left: 56 };
    const innerW = width - margin.left - margin.right;
    const innerH = chartHeight - margin.top - margin.bottom;

    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', chartHeight).attr('viewBox', `0 0 ${width} ${chartHeight}`);

    if (rows.length === 0) {
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', chartHeight / 2)
        .attr('text-anchor', 'middle')
        .attr('fill', '#6c757d')
        .text('No watchlist data');
      return undefined;
    }

    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
    const x = d3
      .scaleLinear()
      .domain([0, Math.max(100, d3.max(rows, (d) => Number(d.unusualness_score) || 0))])
      .nice()
      .range([0, innerW]);
    const y = d3
      .scaleBand()
      .domain(rows.map((d) => d.battery))
      .range([innerH, 0])
      .padding(0.18);

    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(d3.axisBottom(x).ticks(5))
      .call((axis) => axis.selectAll('text').attr('fill', '#495057'))
      .call((axis) => axis.selectAll('line,path').attr('stroke', '#ced4da'));

    g.append('g')
      .call(d3.axisLeft(y).tickSize(0))
      .call((axis) => axis.select('.domain').remove())
      .call((axis) =>
        axis
          .selectAll('text')
          .attr('fill', '#212529')
          .attr('font-size', rows.length > 40 ? 9 : 11),
      );

    g.selectAll('rect.bar')
      .data(rows)
      .join('rect')
      .attr('class', 'bar')
      .attr('x', 0)
      .attr('y', (d) => y(d.battery))
      .attr('height', y.bandwidth())
      .attr('width', (d) => Math.max(0, x(Number(d.unusualness_score) || 0)))
      .attr('fill', (d) => (d.status === 'ANOMALY' ? ANOMALY_COLOR : NORMAL_COLOR))
      .append('title')
      .text(
        (d) =>
          `${d.battery}: score ${d.unusualness_score}`
          + (d.status === 'ANOMALY' ? ' (ANOMALY)' : ''),
      );

    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 30)
      .attr('text-anchor', 'middle')
      .attr('fill', '#6c757d')
      .attr('font-size', 12)
      .text('Unusualness score (0–100)');

    return undefined;
  }, [rows, heightPerBar, minHeight, maxHeight, containerWidth]);

  const anomalyCount = rows.filter((r) => r.status === 'ANOMALY').length;

  return (
    <div className="unusualness-bar-chart" ref={containerRef} style={{ width: '100%', minWidth: 0 }}>
      <div className="d-flex justify-content-between align-items-baseline mb-2 flex-wrap gap-2">
        <div className="fw-semibold">
          How unusual is each battery?
          {series ? ` (series ${series}` : ''}
          {series ? `; ${anomalyCount} flagged)` : anomalyCount ? ` — ${anomalyCount} flagged` : ''}
        </div>
        <div className="small text-muted">
          <span style={{ color: ANOMALY_COLOR }}>■</span>
          {' '}
          anomaly
        </div>
      </div>
      <svg
        ref={svgRef}
        role="img"
        aria-label="Battery unusualness bar chart"
        style={{ display: 'block', maxWidth: '100%' }}
      />
    </div>
  );
}
